import React from 'react';
import useSignedImage from '../../../hooks/useSignedImage';
import './SignedImage.css';

/**
 * Reusable component for displaying images with signed URL support
 * @param {Object} props
 * @param {string} props.src - The image URL (can be signed URL endpoint or direct URL)
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - CSS class name
 * @param {string} props.fallbackSrc - Fallback image URL if loading fails
 * @param {boolean} props.showLoading - Whether to show loading state
 * @param {Function} props.onLoad - Callback when image loads successfully
 * @param {Function} props.onError - Callback when image fails to load
 * @param {Object} props.style - Inline styles
 * @param {Object} props...otherProps - Other props to pass to img element
 */
const SignedImage = ({ 
  src, 
  alt, 
  className = '', 
  fallbackSrc = '/default-restaurant.png',
  showLoading = false,
  onLoad,
  onError,
  style,
  ...otherProps 
}) => {
  const { imageUrl, isLoading, error } = useSignedImage(src, fallbackSrc);

  const handleImageLoad = (e) => {
    if (onLoad) onLoad(e);
  };

  const handleImageError = (e) => {
    if (onError) onError(e);
  };

  return (
    <div className={`signed-image-container ${className}`}>
      {showLoading && isLoading && (
        <div className="signed-image-loading">
          <div className="signed-image-spinner"></div>
        </div>
      )}
      <img
        src={imageUrl}
        alt={alt}
        className={`signed-image ${className}`}
        style={style}
        onLoad={handleImageLoad}
        onError={handleImageError}
        {...otherProps}
      />
      {error && showLoading && (
        <div className="signed-image-error">
          Failed to load image
        </div>
      )}
    </div>
  );
};

export default SignedImage;
